import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Form, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import FalconCloseButton from 'components/common/FalconCloseButton';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';

const MapSearchBox = ({ onSelect, onClear, defaultValue }) => {
  const { placePredictions, getPlacePredictions, placesService } =
    usePlacesService({
      apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
      debounce: 500
    });

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [results] = useState([]);

  useEffect(() => {
    if (!defaultValue) {
      return;
    }
    setSearchInputValue(defaultValue.formatted);
  }, [defaultValue]);

  const handleLocationSelected = location => {
    placesService?.getDetails(
      {
        placeId: location.place_id
      },
      placeDetails => {
        setSearchInputValue(placeDetails.formatted_address);
        const streetNumber = placeDetails.address_components.find(c =>
          c.types.includes('street_number')
        );
        const route = placeDetails.address_components.find(c =>
          c.types.includes('route')
        );

        const street = `${streetNumber?.short_name ?? ''} ${
          route?.short_name ?? ''
        }`;
        const locality = placeDetails.address_components.find(c =>
          c.types.includes('locality')
        );
        const region = placeDetails.address_components.find(c =>
          c.types.includes('administrative_area_level_1')
        );
        const country = placeDetails.address_components.find(c =>
          c.types.includes('country')
        );
        const postalCode = placeDetails.address_components.find(c =>
          c.types.includes('postal_code')
        );
        onSelect({
          id: placeDetails.place_id,
          lat: placeDetails.geometry.location.lat(),
          lng: placeDetails.geometry.location.lng(),
          street: street,
          locality: locality?.short_name,
          region: region?.short_name,
          country: country?.short_name,
          postalCode: postalCode?.short_name,
          formatted: placeDetails.formatted_address
        });
      }
    );
  };

  return (
    <Dropdown
      show={dropdownOpen}
      className="map-search-box"
      onToggle={() => setDropdownOpen(!dropdownOpen)}
    >
      <Dropdown.Toggle as="div" className="dropdown-caret-none">
        <Form.Control
          type="search"
          placeholder="Search..."
          aria-label="Search"
          className="search-input"
          value={searchInputValue}
          onChange={e => {
            const searchText = e.target.value;
            setSearchInputValue(searchText);
            getPlacePredictions({ input: searchText });
          }}
        />
        <FontAwesomeIcon
          icon="search"
          className="position-absolute text-400 search-box-icon"
        />
        {(dropdownOpen || searchInputValue) && (
          <div className="search-box-close-btn-container">
            <FalconCloseButton
              size="sm"
              noOutline
              className="fs-11"
              onClick={() => {
                if (onClear) {
                  onClear();
                }
                setSearchInputValue('');
                setDropdownOpen(false);
              }}
            />
          </div>
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <div className="scrollbar py-3" style={{ maxHeight: '24rem' }}>
          {placePredictions &&
            placePredictions.length > 0 &&
            placePredictions.map(item => (
              <Dropdown.Item
                className="fs-10 px-x1 py-1 hover-primary"
                key={item.place_id}
                onClick={() => handleLocationSelected(item)}
              >
                <Flex alignItems="center">
                  <FontAwesomeIcon
                    icon="location-dot"
                    className="me-2 text-300 fs-11"
                  />
                  <div className="fw-normal">{item.description}</div>
                </Flex>
              </Dropdown.Item>
            ))}
        </div>
        {(!placePredictions || placePredictions.length == 0) && (
          <div className="text-center mt-n3">
            <p
              className={classNames('fs-8 fw-bold text-center', {
                'd-none': results.length > 0
              })}
            >
              No Result Found.
            </p>
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

MapSearchBox.propTypes = {
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  defaultValue: PropTypes.object
};

export default MapSearchBox;
